// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Search, Filter, DollarSign, CheckCircle, XCircle, Edit, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  admin: string;
  payee_name: string;
  payee_type: 'vendor' | 'press' | 'scout' | 'affiliate';
  amount: number | null;
  status: 'completed' | 'pending' | 'failed';
  notes: string;
  metadata: Record<string, any>;
}

const AuditTrailTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [payeeTypeFilter, setPayeeTypeFilter] = useState('all');

  // Mock data - replace with actual query
  const auditLog: AuditEntry[] = [
    {
      id: '1',
      timestamp: '2024-01-15T10:30:00Z',
      action: 'payout_processed',
      admin: 'Mike Smith',
      payee_name: 'John Graphics',
      payee_type: 'vendor',
      amount: 5000,
      status: 'completed',
      notes: 'Monthly payout for completed orders',
      metadata: { payment_method: 'stripe', transaction_id: 'pi_123456' },
    },
    {
      id: '2',
      timestamp: '2024-01-15T11:15:00Z',
      action: 'commission_adjusted',
      admin: 'Mike Smith',
      payee_name: 'Jane Writer',
      payee_type: 'press',
      amount: 150,
      status: 'completed',
      notes: 'Bonus for exceptional article quality',
      metadata: { adjustment_type: 'bonus', approval: 'approved' },
    },
    {
      id: '3',
      timestamp: '2024-01-15T14:00:00Z',
      action: 'tax_form_uploaded',
      admin: 'Mike Smith',
      payee_name: 'Scout Bob',
      payee_type: 'scout',
      amount: null,
      status: 'completed',
      notes: 'W-9 form received and validated',
      metadata: { form_type: 'w9', document_id: 'doc_789' },
    },
    {
      id: '4',
      timestamp: '2024-01-15T15:45:00Z',
      action: 'payout_failed',
      admin: 'System',
      payee_name: 'Affiliate Alex',
      payee_type: 'affiliate',
      amount: 2500,
      status: 'failed',
      notes: 'Insufficient Stripe balance',
      metadata: { error_code: 'insufficient_funds', retry_scheduled: true },
    },
  ];

  const filteredAudit = auditLog.filter(entry => {
    const matchesSearch = 
      entry.payee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.admin.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = actionFilter === 'all' || entry.action === actionFilter;
    const matchesPayeeType = payeeTypeFilter === 'all' || entry.payee_type === payeeTypeFilter;

    return matchesSearch && matchesAction && matchesPayeeType;
  });

  const exportAuditLog = () => {
    const headers = ['Timestamp', 'Action', 'Admin', 'Payee', 'Type', 'Amount', 'Status', 'Notes'];
    const rows = filteredAudit.map(entry => [
      new Date(entry.timestamp).toLocaleString(),
      entry.action,
      entry.admin,
      entry.payee_name,
      entry.payee_type,
      entry.amount ? `$${entry.amount.toFixed(2)}` : 'N/A',
      entry.status,
      entry.notes,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Audit trail exported');
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'payout_processed':
        return <DollarSign className="h-4 w-4 text-green-600" />;
      case 'commission_adjusted':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'tax_form_uploaded':
        return <FileText className="h-4 w-4 text-purple-600" />;
      case 'payout_failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'destructive' | 'secondary'> = {
      completed: 'default',
      pending: 'secondary',
      failed: 'destructive',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{auditLog.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {auditLog.filter(e => e.status === 'completed').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {auditLog.filter(e => e.status === 'failed').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {auditLog.filter(e => e.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Financial Actions Audit Trail</CardTitle>
              <CardDescription>Complete log of all financial transactions and actions</CardDescription>
            </div>
            <Button onClick={exportAuditLog}>
              <Download className="h-4 w-4 mr-2" />
              Export Log
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by payee, action, or admin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="payout_processed">Payout Processed</SelectItem>
                <SelectItem value="commission_adjusted">Commission Adjusted</SelectItem>
                <SelectItem value="tax_form_uploaded">Tax Form Uploaded</SelectItem>
                <SelectItem value="payout_failed">Payout Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={payeeTypeFilter} onValueChange={setPayeeTypeFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="vendor">Vendors</SelectItem>
                <SelectItem value="press">Press</SelectItem>
                <SelectItem value="scout">Scouts</SelectItem>
                <SelectItem value="affiliate">Affiliates</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Payee</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAudit.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(entry.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getActionIcon(entry.action)}
                      <span className="font-medium">{entry.action.replace(/_/g, ' ')}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{entry.admin}</TableCell>
                  <TableCell>{entry.payee_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{entry.payee_type}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {entry.amount ? formatCurrency(entry.amount) : 'N/A'}
                  </TableCell>
                  <TableCell>{getStatusBadge(entry.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {entry.notes}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Compliance Note */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-sm">Compliance & Record Retention</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">
            This audit trail is automatically backed up daily and retained for 7 years to comply with IRS regulations.
            All financial actions are logged with timestamps, admin identity, and full transaction details for complete transparency and compliance.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditTrailTab;
